/**
 * RecQuant - a row of Live RECORD-quantization buttons.
 *
 * ⚠️ RECORD quantization, not global LAUNCH quantization. The two vocabularies
 * share most of their label text and have different enum orders; crossing
 * them is a wrong result, never an error. This component imports
 * `abletonRecQuant.ts` and nothing else. A launch-quant row would be its own
 * component reading the other module - see the warning at the top of either
 * schema file before "simplifying" that.
 *
 * WHAT LIGHTS, and why it matters:
 *
 *   lit cell     = the DEVICE readback (`/clock/status.rec_quant`), never
 *                  what we last sent. An optimistic highlight desyncs the
 *                  instant the operator changes the menu in Live itself, and
 *                  then shows the wrong quantization with full confidence.
 *   pending cell = we sent this and the readback has not agreed yet. Clears
 *                  the moment it does; after REC_QUANT_CONFIRM_MS it stops
 *                  claiming to be in flight and just sits un-lit.
 *   nothing lit  = Live's current value is not one of this row's labels, or
 *                  we have never heard from the device at all. Both are
 *                  honest and both are worth seeing.
 *
 * That last case is the one that earns the pending state: OPBridge returns a
 * cheerful 200 for a send with no M4L device loaded (the UDP datagram leaves
 * happily and Ableton never hears it), so a green ack proves nothing. The
 * round trip is the only evidence. Same posture OPBridge's own dashboard
 * control takes for this exact device (their DEC-547).
 *
 * Shares the Transpose row's visuals (`.transpose` / `.transpose-cell`) -
 * one skinned primitive, three items, per skin-components.css 11k.
 *
 * OP-QUANTIZE (`item.quantizer === 'op-quantize'`, OPM4LBuilder ask
 * 2026-10-10): the same labels go to the soft OP-Quantize device instead,
 * and an Amount cell is added at the end of the row (a number dragged up or
 * down, 2026-10-15; it was a band under the pads). Live's own readback is now
 * ALWAYS None (the device holds it there), so the row lights from the
 * DEVICE'S report instead: OPBridge relays every instance's
 * `/OP-Quantize/state` as `/clock/status.op_quantize`, with a set-wide
 * `grid` and `amount` when all instances agree. That is also how a song
 * change shows: OPSetlist's `quant` tag reaches the devices through OPBridge
 * (`set_grid`), never through this row, and the row follows it.
 *
 *   mixed (instances disagree) = nothing lit, Amount reads "mixed".
 *   never heard (an older OPBridge, or no instance loaded) = the row shows
 *   what it last SENT, drawn as `data-sent` (a lit edge, never the live
 *   flood), and its tooltip says the device has not reported.
 *
 * LIVE MUST STAY AT NONE UNDER OP-QUANTIZE (operator, 2026-10-15). The device
 * sets Live's own record quantize to None once, at load; if anything turns it
 * back on, OP-Quantize silently stops working. So an OP-Quantize tap also
 * sends Live None (`recQuantSends`), and the row WARNS (`data-warn`, the
 * fault colour) whenever Live's readback says its own quantize is on:
 *   OP-Quantize row: always, since that row needs Live at None. Any tap here
 *     fixes it.
 *   Live row: only while OP-Quantize devices are reporting, since that is
 *     when Live's quantize fights them. The row still sends what is pressed.
 */

import { For, Show, createSignal, onCleanup } from 'solid-js';
import { editOwnsGesture, editItem, type RecQuantItem, type LayoutColor } from '../layout/store.ts';
import { REC_QUANT_CONFIRM_MS } from '../layout/projectStore.ts';
import { REC_QUANT_DISPLAY, normalizeRecQuant, type RecQuantLabel } from '@opbuttons/schema';
import { abletonState, pokeClockPoll } from '../opbridge/clockPoll.ts';
import { fireAction, fanoutTargets, sendOpQuantizeAmount, OP_QUANTIZE_DEFAULT_AMOUNT } from '../opbridge/dispatch.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { ulid } from '../utils/ulid.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

interface RecQuantProps {
  item: RecQuantItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Face text, split into up to two lines.
 *
 *  The two combo labels ("1/8 + 1/8T") are three times the width of the rest
 *  of the vocabulary. Sizing every cell for the worst case would make a row
 *  of six values wider than the iPad; shrinking the text to fit would make
 *  the common values unreadable at arm's length on a dark stage. So the
 *  combos - and only the combos - wrap. */
function face(label: RecQuantLabel): { l1: string; l2: string | null } {
  if (label === '8+8T') return { l1: '1/8', l2: '+1/8T' };
  if (label === '16+16T') return { l1: '1/16', l2: '+1/16T' };
  return { l1: REC_QUANT_DISPLAY[label], l2: null };
}

/** Screen pixels of vertical drag per 1% of Amount: the whole range is
 *  about one thumb-length on the iPad. */
const AMOUNT_PX_PER_PERCENT = 2;
/** Movement below this is a tap, not a drag. */
const AMOUNT_DRAG_SLOP_PX = 4;
/** Two taps this close reset Amount to the default. */
const AMOUNT_DOUBLE_TAP_MS = 350;

/** Clamp to 0-100 and snap to 5% steps, so 80 is easy to land on mid-gig. */
export function snapAmount(v: number): number {
  return Math.max(0, Math.min(100, Math.round(v / 5) * 5));
}

export function RecQuant(props: RecQuantProps) {
  // Show-mode guard (SD-5): first tap arms the cell (2s), second sends.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  // The label we last sent and have not yet seen confirmed. Cleared by the
  // readback agreeing, or by the timeout - never left set, because a cell
  // stuck claiming "in flight" forever is worse than one that says nothing.
  const [pending, setPending] = createSignal<RecQuantLabel | null>(null);
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  onCleanup(() => { if (pendingTimer !== null) clearTimeout(pendingTimer); });

  /** Live's current record quantization as the device reports it, or null
   *  when we have not heard (which is NOT the same as Live saying None -
   *  "0" is a real label meaning off). */
  const isOpq = (): boolean => props.item.quantizer === 'op-quantize';

  /** The OP-Quantize devices' own report, or null when never heard. */
  const opq = () => (isOpq() ? abletonState().opQuantize : null);

  /** Live's own record quantize when it is ON and that breaks OP-Quantize,
   *  else null. Unknown (never heard) is not a warning: a light claims only
   *  what it knows (DEC-124). */
  const liveFights = (): RecQuantLabel | null => {
    const live = normalizeRecQuant(abletonState().recQuant?.label);
    if (live === null || live === '0') return null;
    return isOpq() || abletonState().opQuantize ? live : null;
  };

  const observed = (): RecQuantLabel | null =>
    isOpq() ? normalizeRecQuant(opq()?.grid) : normalizeRecQuant(abletonState().recQuant?.label);

  // A pending send that the readback has caught up with is no longer
  // pending. Reading `observed()` here inside the accessor keeps this
  // derived rather than an effect that writes a signal it also reads.
  const pendingLabel = (): RecQuantLabel | null => {
    const p = pending();
    return p !== null && observed() !== p ? p : null;
  };

  function onCellDown(label: RecQuantLabel, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    if (!guard.gate(label)) return; // guarded: first tap only armed

    setPending(label);
    if (pendingTimer !== null) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => setPending(null), REC_QUANT_CONFIRM_MS);

    for (const target of fanoutTargets(props.item.bridgeIds)) {
      void fireAction(isOpq()
        ? { id: ulid(), type: 'ableton.recquant', label, via: 'op-quantize', target }
        : { id: ulid(), type: 'ableton.recquant', label, target });
    }
    // Still remembered: it is what the row shows while the devices have
    // never reported (see the header).
    if (isOpq() && props.item.sentLabel !== label) editItem(props.item.id, { sentLabel: label } as Partial<RecQuantItem>);
    // Don't wait out the poll interval to find out whether it landed - the
    // whole point of the pending state is that it resolves fast when the
    // device IS there.
    pokeClockPoll();
  }

  // ─── Amount (OP-Quantize only) ───────────────────────────────────────
  // The last cell of the row: a number, dragged UP or DOWN (operator,
  // 2026-10-15). Not guarded - a level is correctable by dragging back. The
  // number follows the finger at once in 5% steps; it is SENT on release
  // only, once, because every OP-Quantize instance (one per track) hears
  // every packet, and no take is recorded mid-drag anyway. A tap without a
  // drag sends nothing; a double-tap resets to the operator's default.
  const [dragAmount, setDragAmount] = createSignal<number | null>(null);
  /** The devices' amount when they agree; `'mixed'` when they do not; the
   *  last amount sent from here when they have never reported. */
  const shownAmount = (): number | 'mixed' | null => {
    const d = dragAmount();
    if (d !== null) return d;
    const snap = opq();
    if (snap) return snap.amount ?? 'mixed';
    return props.item.amount ?? null;
  };
  const amountFill = (): number => { const a = shownAmount(); return typeof a === 'number' ? a : 0; };

  let dragFrom: { y: number; value: number; moved: boolean } | null = null;
  let lastTapAt = 0;

  function sendAmount(v: number) {
    for (const target of fanoutTargets(props.item.bridgeIds)) void sendOpQuantizeAmount(v, target);
    if (props.item.amount !== v) editItem(props.item.id, { amount: v } as Partial<RecQuantItem>);
    pokeClockPoll();
  }

  function onAmountDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    const now = shownAmount();
    dragFrom = { y: e.clientY, value: typeof now === 'number' ? now : (props.item.amount ?? OP_QUANTIZE_DEFAULT_AMOUNT), moved: false };
  }

  function onAmountMove(e: PointerEvent) {
    if (!dragFrom) return;
    e.stopPropagation();
    const dy = dragFrom.y - e.clientY;
    if (!dragFrom.moved && Math.abs(dy) < AMOUNT_DRAG_SLOP_PX) return;
    dragFrom.moved = true;
    setDragAmount(snapAmount(dragFrom.value + dy / AMOUNT_PX_PER_PERCENT));
  }

  function onAmountUp(e: PointerEvent) {
    const from = dragFrom;
    dragFrom = null;
    if (!from) return;
    e.stopPropagation();
    const v = dragAmount();
    setDragAmount(null);
    if (from.moved) {
      if (v !== null) sendAmount(v);
      lastTapAt = 0;
      return;
    }
    const now = Date.now();
    if (now - lastTapAt <= AMOUNT_DOUBLE_TAP_MS) {
      lastTapAt = 0;
      sendAmount(OP_QUANTIZE_DEFAULT_AMOUNT);
    } else {
      lastTapAt = now;
    }
  }

  function onAmountCancel() {
    dragFrom = null;
    setDragAmount(null);
  }

  function cellTitle(label: RecQuantLabel): string {
    const live = liveFights();
    const warn = live === null ? ''
      : isOpq() ? `Live's own record quantize is on (${REC_QUANT_DISPLAY[live]}), so OP-Quantize cannot work; any tap here sets it back to None. `
      : `OP-Quantize is loaded and Live's own record quantize (${REC_QUANT_DISPLAY[live]}) stops it working; tap None, or use an OP-Quantize row. `;
    return warn + cellPurpose(label);
  }

  function cellPurpose(label: RecQuantLabel): string {
    const what = label === '0' ? 'off' : `to ${REC_QUANT_DISPLAY[label]}`;
    if (!isOpq()) {
      return label === '0' ? 'Record quantization OFF (Live’s None)' : `Record quantize ${what}`;
    }
    if (!opq() && props.item.sentLabel === label) return `OP-Quantize: last set ${what} from here (the device has not reported back)`;
    if (opq() && !opq()!.grid) return `OP-Quantize: tracks are on different grids; quantize new notes ${what}`;
    return `OP-Quantize: quantize new notes ${what}`;
  }

  return (
    <div
      class="transpose recquant"
      data-token={COLOR_TOKEN[props.item.color]}
      data-quantizer={isOpq() ? 'op-quantize' : 'live'}
      data-warn={liveFights() ? 'true' : 'false'}
      {...trayAttrs(props.item)}
    >
      <TrayTitle item={props.item} />
      <div class="recquant-cells">
        <For each={props.item.labels}>
          {(label) => {
            const f = face(label);
            return (
              <button
                type="button"
                class="transpose-cell recquant-cell"
                data-active={observed() === label ? 'true' : 'false'}
                data-pending={pendingLabel() === label ? 'true' : 'false'}
                data-sent={isOpq() && !opq() && props.item.sentLabel === label ? 'true' : 'false'}
                data-armed={guard.armedKey() === label ? 'true' : 'false'}
                onPointerDown={(e) => onCellDown(label, e)}
                title={cellTitle(label)}
              >
                <span class="rq-l1">{f.l1}</span>
                {f.l2 !== null && <span class="rq-l2">{f.l2}</span>}
              </button>
            );
          }}
        </For>
        <Show when={isOpq()}>
          <div
            class="transpose-cell recquant-amount"
            role="slider"
            aria-label="OP-Quantize amount"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={amountFill()}
            data-dragging={dragAmount() !== null ? 'true' : 'false'}
            data-mixed={shownAmount() === 'mixed' ? 'true' : 'false'}
            onPointerDown={onAmountDown}
            onPointerMove={onAmountMove}
            onPointerUp={onAmountUp}
            onPointerCancel={onAmountCancel}
            title="OP-Quantize amount: how far new notes move toward the grid. Drag up or down, sent when you let go; double-tap for 80%."
          >
            <div class="recquant-amount-fill" style={{ height: `${amountFill()}%` }} />
            {/* "--" when nothing is known (never reported, never sent):
                0 would claim "no quantize". */}
            <span class="recquant-amount-value">
              {shownAmount() === null ? '--' : shownAmount() === 'mixed' ? 'mix' : `${shownAmount()}%`}
            </span>
            <span class="recquant-amount-label">AMT</span>
          </div>
        </Show>
      </div>
      <Show when={liveFights()}>
        {(live) => (
          <span class="recquant-warn">
            {isOpq() ? `Live quantize on (${REC_QUANT_DISPLAY[live()]}): tap a value` : 'Blocks OP-Quantize'}
          </span>
        )}
      </Show>
    </div>
  );
}
